/**
 * CR305+CR310: Recipe Picker Bar (modeled on Layout Studio's PresetPickerBar)
 * CR310: Added template badge in picker + "from template" option in new recipe dialog
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ChevronDown,
  Plus,
  Copy,
  Archive,
  Settings2,
  Clock,
  LayoutTemplate,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Recipe, RecipeScope } from "@/lib/recipes-studio/types";

interface RecipePickerBarProps {
  recipes: Recipe[];
  selectedId: string | null;
  scopeFilter: RecipeScope | 'ALL';
  onSelectRecipe: (id: string) => void;
  onCreateNew: (name: string, fromTemplateId?: string) => void;
  onClone: () => void;
  onArchive: () => void;
  onManageRecipes: () => void;
  onScopeChange: (scope: RecipeScope | 'ALL') => void;
}

export function RecipePickerBar({
  recipes,
  selectedId,
  scopeFilter,
  onSelectRecipe,
  onCreateNew,
  onClone,
  onArchive,
  onManageRecipes,
  onScopeChange,
}: RecipePickerBarProps) {
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("__none__");
  const selectedRecipe = recipes.find(r => r.id === selectedId);

  // CR310: Templates available for "new from template"
  const templates = recipes.filter(r => r.is_template);

  const handleCreate = () => {
    if (newName.trim()) {
      const templateId = selectedTemplateId === '__none__' ? undefined : selectedTemplateId;
      onCreateNew(newName.trim(), templateId);
      setNewName("");
      setSelectedTemplateId("__none__");
      setShowNewDialog(false);
    }
  };

  const handleOpenNewDialog = () => {
    setNewName("");
    setSelectedTemplateId("__none__");
    setShowNewDialog(true);
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4 px-4 py-2 border-b bg-card">
        {/* Left: Recipe Selector */}
        <div className="flex items-center gap-3">
          <Select
            value={selectedId || ""}
            onValueChange={(value) => value && onSelectRecipe(value)}
          >
            <SelectTrigger className="w-[280px] h-9">
              <SelectValue placeholder="Select a recipe...">
                {selectedRecipe && (
                  <div className="flex items-center gap-2">
                    <span className="truncate">{selectedRecipe.recipe_name}</span>
                    {selectedRecipe.is_template && (
                      <Badge variant="default" className="text-[9px] px-1 py-0">
                        TPL
                      </Badge>
                    )}
                    <Badge variant="secondary" className="text-[9px] px-1 py-0">
                      {selectedRecipe.recipe_scope}
                    </Badge>
                  </div>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-popover border shadow-lg z-50">
              {recipes.map((recipe) => (
                <SelectItem key={recipe.id} value={recipe.id}>
                  <div className="flex items-center gap-2">
                    <span className="truncate">{recipe.recipe_name}</span>
                    {recipe.is_template && (
                      <Badge variant="default" className="text-[9px] px-1 py-0">
                        TPL
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[9px] px-1 py-0">
                      {recipe.recipe_scope}
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Actions Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 px-2">
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-popover border shadow-lg z-50">
              <DropdownMenuItem onClick={handleOpenNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                New Recipe
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onClone} disabled={!selectedId}>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onArchive} disabled={!selectedId}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onManageRecipes}>
                <Settings2 className="h-4 w-4 mr-2" />
                Manage All Recipes
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Right: Scope filter + last updated */}
        <div className="flex items-center gap-3">
          <Select value={scopeFilter} onValueChange={(v) => onScopeChange(v as RecipeScope | 'ALL')}>
            <SelectTrigger className="w-[140px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border shadow-lg z-50">
              <SelectItem value="WEB_CRAWL">Web Crawl</SelectItem>
              <SelectItem value="FLYER_FETCH">Flyer Fetch</SelectItem>
              <SelectItem value="ALL">All Scopes</SelectItem>
            </SelectContent>
          </Select>

          {selectedRecipe && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-xs">
                Updated {formatDistanceToNow(new Date(selectedRecipe.updated_at), { addSuffix: true })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* New Recipe Dialog — CR310: with optional template selector */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>New Recipe</DialogTitle>
            <DialogDescription>
              Create a new recipe, optionally starting from a template.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipe Name</Label>
              <Input
                placeholder="My Web Crawl Recipe"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5">
                <LayoutTemplate className="h-3 w-3" />
                From Template (optional)
              </Label>
              <Select
                value={selectedTemplateId}
                onValueChange={setSelectedTemplateId}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="None — blank recipe" />
                </SelectTrigger>
                <SelectContent className="bg-popover border shadow-lg z-50">
                  <SelectItem value="__none__">None — blank recipe</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <div className="flex items-center gap-2">
                        <span>{t.recipe_name}</span>
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          {t.recipe_scope}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {templates.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  No templates yet. Mark a recipe as "Template" in its Basics section.
                </p>
              )}
              {selectedTemplateId !== '__none__' && (
                <p className="text-xs text-muted-foreground">
                  Config, actions, consent, and offer-safe settings will be copied. Start URL will be cleared.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newName.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
